// The batches — the production runs the model library grew in. This is the
// single source of truth a model's `batchId` resolves against: the viewer
// labels a version by its batch, the MCP server lists batches and stamps each
// model's provenance, and the registry guardrail asserts every `batchId` points
// at one of these. The set is small and rarely changes, so it lives in one
// hand-written file (no discovery) — it imports only the library, so the viewer
// (Vite) and the server/tests (tsx) load it identically.
//
// The set spans the look tiers the game shipped (Standard -> Fable -> 45
// Optimized -> Polished) plus content series that are this repo's own (Future,
// Electric, Characters). A batch is provenance + a generation brief: its `style`
// is the look it was authored under, `for` records the game it targets, and —
// for every Age of MCP batch — its `description` carries the full AGE OF MCP
// generation contract below, so an agent reading a single batch (via the MCP
// `list_batches` tool or the viewer's batch page) has everything it needs to
// generate a model that drops straight into the game.

import { defineBatch, type Batch } from "../lib";

// The full Age of MCP model contract — the binding requirements a generated
// model must satisfy to be usable in the game. It is the SAME for every Age of
// MCP batch, so it is written once here and spread into each batch's
// `description` (the data still repeats per batch — that is the point: each
// batch is self-contained — but the source is single-authored so it cannot
// drift between batches). Per-batch notes are prepended; the contract follows.
// Authored as Markdown (headings, lists, `code`, **bold**) so the viewer's
// "Instructions" fold reads as a structured document. It is shown as plain text
// (white-space: pre-wrap), not rendered — the Markdown is the structure, on view.
const AOM_CONTRACT = [
  "# Age of MCP generation contract",
  "",
  "What a model must satisfy to drop into the game.",
  "",
  "## Part language",
  "Low-poly only. Each part is one three.js primitive (`Box`, `Cylinder`, " +
    "`Cone`, `Icosahedron`, `Sphere`) painted one flat colour — no textures, no " +
    "gradients. A model is a flat `Part[]` authored through `defineModel`; shading " +
    "is faceted and unlit (baked per face by `buildModel`).",
  "",
  "## Frame and scale",
  "- **Up:** `y` is up and `y=0` is the ground (the model sits on the floor).",
  "- **Forward:** `+Z` = north, `+X` = east.",
  "- **Scale:** 1 tile = 5 metres; author in tile units (fractions are fine).",
  "",
  "## Footprint",
  "Set `tiles {w,h}` to the object's footprint from age-of-mcp's " +
    "`buildings-catalog.ts` (typically 1x1, 2x2, 3x3 or 4x4). Author the model at " +
    "its **true footprint size**, centred on the origin, to sit at scale 1 " +
    "(footprint-native) — never author a 1x1 and rely on the game scaling it up. " +
    "`footprint` (a radius) frames the camera and sizes the ground shadow.",
  "",
  "## Build stages (buildings only)",
  "A building is staged as four variants:",
  "",
  "1. `foundation` — bare slab",
  "2. `lower` — slab + scaffold + partial walls",
  "3. `upper` — taller walls + scaffold",
  "4. `complete` — finished, no scaffold",
  "",
  "List them foundation-first and set `defaultVariant: \"complete\"`. Scenery, " +
    "props, creatures and vehicles are not staged (one variant, or extra visual " +
    "variants for diversity).",
  "",
  "## Top-down legibility",
  "The game uses a 45-degree orthographic camera, so a building's identity must " +
    "read from above — **the roof is the sign** (function-coded roof colour + top " +
    "silhouette + one oversized iconic feature), and every work/eat/load spot " +
    "stands on open, uncovered ground. The game draws each tile object as one " +
    "ASCII glyph in `look` (uppercase = done, lowercase = under construction); the " +
    "glyph lives in the game catalog, but the model must read clearly in silhouette.",
  "",
  "## Palette",
  "Flat, saturated, stylised. Reuse the shared functional palette:",
  "",
  "- **terracotta roof** = homes/civic",
  "- **wheat** = food chain",
  "- **leaf green** = wood industry",
  "- **slate** = stone/mineral",
  "- **gold** = precious",
  "- **hazard yellow** = logistics/fuel",
  "- **banner red** = trade/special",
  "",
  "Sample materials: bark `#6b4a2a`, stone `#8d8a85`, plank `#c9a36a`, gold " +
    "`#e7b53a`, crystal `#67c4d8`, wood wall `#8b6438`, plaster `#e3d5b8`.",
  "",
  "## Part budget",
  "Keep it lean — scenery <= ~35 parts, creatures <= ~45, buildings <= ~140 per " +
    "stage (45-optimized buildings cap at 60, target ~40). Prefer one long box " +
    "over many small bits, one-part prism/pyramid roofs.",
  "",
  "## Identity and scale parity",
  "A re-render of an existing object keeps its identity (the same id stem) and " +
    "stays within +-25% of the reference height, with a fresh silhouette — never " +
    "the old parts rearranged.",
  "",
  "## Worker use",
  "When an actor works/eats/loads at the model: publish **Station(s)** — a " +
    "model-local position, facing and capacity where the actor stands — and an " +
    "**Interaction** coupling the worker rig to the model, built from the ported " +
    "programs (`benchWork`, `sitEat`, `pace`, `leanLoad`, `climb`, `standVisit`, " +
    "`swing`). Re-author the stations at this model's own geometry.",
  "",
  "## Naming",
  "The model id is the in-game object id (`house`, `sawmill`, `rabbit`, ...); a " +
    "re-render adds a `-vN` suffix on the same stem. Tags are lowercase.",
  "",
  "## Verify by looking",
  "You cannot judge a model from its code — render it headless " +
    "(`npm run preview-model -- <id>`) and look at the PNG before calling it done.",
].join("\n");

/** Compose a batch description: optional per-batch notes, then the shared
 *  Age of MCP generation contract (so each batch carries the full contract). */
function withContract(notes?: string): string {
  return notes ? `${notes}\n\n${AOM_CONTRACT}` : AOM_CONTRACT;
}

// The Modern Living construction-kit contract — the binding requirements for
// the `modern` batch. Unlike the Age of MCP batches this kit targets no game:
// it is a modular, grid-snapped kit of present-day pieces (structure,
// furniture, appliances, fixtures, decor, outdoor/city) from which whole
// real-world places — a family home, an apartment, a fast-food restaurant, a
// church, an office, a park, a street grid — can be assembled tile by tile.
const MODERN_CONTRACT = [
  "# Modern Living construction-kit contract",
  "",
  "What a model must satisfy to snap into the kit.",
  "",
  "## Purpose",
  "A modular kit of contemporary, real-world pieces. Everything is authored " +
    "on one shared tile grid so a consumer can assemble complete places — " +
    "houses, apartments, restaurants, offices, churches, gardens, parks, " +
    "streets, whole city blocks — by placing tiles side by side. A piece " +
    "never assumes a specific building: it is generic (a wall segment, a " +
    "sofa, a road tile) and combines freely.",
  "",
  "## Part language",
  "Low-poly only. Each part is one three.js primitive (`Box`, `Cylinder`, " +
    "`Cone`, `Icosahedron`, `Sphere`) painted one flat colour — no textures, " +
    "no gradients. A model is a flat `Part[]` authored through `defineModel`.",
  "",
  "## Frame and scale",
  "- **Up:** `y` is up and `y=0` is the floor (every model sits on the floor).",
  "- **Forward:** `+Z` = north, `+X` = east.",
  "- **Scale:** **1 tile = 1 metre**; author at true real-world size in " +
    "metres, centred on the origin.",
  "- Every model sets `tiles {w,h}` — the whole tiles it occupies (a sofa " +
    "2x1, a road tile 1x1, a house roof span 4x1).",
  "",
  "## Structure conventions (walls, floors, stairs, roofs)",
  "These make separately-authored pieces meet cleanly:",
  "",
  "- **Walls** run along the **X axis** through the tile centre, exactly " +
    "`w` tiles long (full tile length, so consecutive wall tiles form a " +
    "continuous wall), **0.12 thick**, standard height **2.6**. The consumer " +
    "rotates in 90-degree steps to orient. Corner / T / cross junctions are " +
    "their own 1x1 pieces joining half-length walls at the tile centre.",
  "- **Openings:** door openings are 0.9 wide x 2.05 high; window sills sit " +
    "at 0.9 with the head at 2.1.",
  "- **Floors** are 1x1 slabs exactly 1.0 x 1.0, **0.06 thick** (top surface " +
    "at y=0.06), so any floor tiles seamlessly against any other.",
  "- **Stairs** occupy 1x2 tiles and climb one storey (2.6) along +Z; " +
    "landings are floor tiles.",
  "- **Roof pieces** tile along X (ridge on X); eaves meet at y=2.6.",
  "- Outdoor ground (grass, paths, roads, sidewalks) uses the same 1x1 " +
    "0.06-thick slab convention, so streets and gardens tile against floors.",
  "",
  "## Palette",
  "Contemporary and muted — flat colours that read as today's materials: " +
    "plaster white `#e8e4dc`, warm grey `#b8b2a8`, concrete `#9a9a94`, oak " +
    "`#c8975a`, walnut `#7a5a3a`, matte black `#2e2e30`, steel `#aab2b8`, " +
    "glass `#9fc4d4`, asphalt `#3f4044`, sidewalk `#b5b0a6`, lawn `#6fae4e`, " +
    "brick `#a05a40`. One or two muted accent colours per model at most.",
  "",
  "## Part budget",
  "Structure pieces <= 12 parts, furniture and fixtures <= 25, appliances " +
    "<= 20, larger outdoor set pieces <= 40. Prefer one long box over many " +
    "small bits.",
  "",
  "## Variants and animations",
  "Single-variant by default. Add variants only for a state a builder " +
    "genuinely switches (a door open/closed, a TV on/off); add animations " +
    "sparingly for signature motion (a ceiling fan, a swinging gate).",
  "",
  "## Naming and tags",
  "Ids are kebab-case nouns (`wall-window`, `park-bench`). Tags are " +
    "lowercase and always include the kit tag `modern`, the setting " +
    "(`interior` and/or `outdoor`) and function tags (`seating`, `kitchen`, " +
    "`road`, `garden`, ...).",
  "",
  "## Verify by looking",
  "You cannot judge a model from its code — render it headless " +
    "(`npm run preview-model -- <id>`) and look at the PNG before calling " +
    "it done.",
].join("\n");

// The Cars showcase contract — the binding requirements for the `cars` batch:
// a garage of real production cars, each pushed to the most detailed,
// realistic read the low-poly part language allows.
const CARS_CONTRACT = [
  "# Cars showcase contract",
  "",
  "What a model must satisfy to join the cars batch.",
  "",
  "## Purpose",
  "A showcase garage of real production cars. Each model reproduces one " +
    "recognisable, real-world car as faithfully as the low-poly part language " +
    "allows — true proportions taken from the manufacturer's spec sheet, full " +
    "exterior detail and a finish that reads as the actual car, not a generic toy.",
  "",
  "## Part language",
  "Low-poly only. Each part is one three.js primitive (`Box`, `Cylinder`, " +
    "`Cone`, `Icosahedron`, `Sphere`, `Torus`) painted one flat colour — no " +
    "textures, no gradients. A model is a flat `Part[]` authored through " +
    "`defineModel`.",
  "",
  "## Frame and scale",
  "- **Up:** `y` is up and `y=0` is the ground (the tyres touch the floor).",
  "- **Forward:** `+Z` is the car's nose.",
  "- **Scale:** **1 unit = 1 metre**; author at the car's true dimensions " +
    "(length, width, height, wheelbase, wheel diameter from the spec sheet).",
  "",
  "## Detail expectations",
  "This batch trades the game batches' tight part budgets for fidelity " +
    "(up to ~220 parts per car). A car should carry:",
  "",
  "- a correctly massed body: bumpers, bonnet, raked windscreen and rear " +
    "glass, tumblehome side glazing, pillars, sills and wheel-arch cladding;",
  "- a real front fascia (grille with slats and badge, headlamps with inner " +
    "detail, lower intake, fog lamps, number plate) and rear fascia (light " +
    "clusters, plate, skid plate or diffuser);",
  "- wheels with tyres, rims, spokes, centre caps and visible brake discs;",
  "- furniture: mirrors, door handles, roof rails, wipers, antenna, spoiler.",
  "",
  "## Paint slot",
  "Every painted body panel carries `slot: \"paint\"` so a consumer recolours " +
    "the whole car with one colour; the part's own colour is the factory " +
    "default shade. Trim (black cladding, chrome, glass, lights) stays " +
    "unslotted.",
  "",
  "## Animations",
  "Give every car a looping `drive` animation — all four wheels spinning " +
    "about their axles; name each wheel's parts `wheel-fl` / `wheel-fr` / " +
    "`wheel-rl` / `wheel-rr`. Signature extras (hazard lights, an opening " +
    "tailgate) are welcome as separate clips.",
  "",
  "## Naming and tags",
  "The id is the real car's kebab-case name (`skoda-karoq`); tags are " +
    "lowercase and include `car`, the make and the body style (`suv`, ...).",
  "",
  "## Verify by looking",
  "You cannot judge a model from its code — render it headless " +
    "(`npm run preview-model -- <id>`) and look at the PNG before calling " +
    "it done.",
].join("\n");

// The Mess contract — the binding requirements for the `mess` batch:
// a characterful, Pixar-like 3D-animation-film look, applied to models useful
// across many games (cute creatures, whimsical buildings, fairground rides,
// toy-like vehicles, magic props).
const STORYBOOK_CONTRACT = [
  "# Mess generation contract",
  "",
  "What a model must satisfy to join the mess batch.",
  "",
  "## Purpose",
  "A general-purpose set in the feel of a Pixar-style 3D animation film: " +
    "every model is a character, even the buildings. Chunky rounded " +
    "silhouettes, exaggerated toy-like proportions, warm saturated colours " +
    "and life through animation. Nothing is tied to one game — the set spans " +
    "cute creatures, whimsical buildings, fairground rides, adventure " +
    "vehicles and magic props any game can drop in.",
  "",
  "## Part language",
  "Low-poly only. Each part is one three.js primitive (`Box`, `Cylinder`, " +
    "`Cone`, `Icosahedron`, `Sphere`, `Torus`) painted one flat colour — no " +
    "textures, no gradients. A model is a flat `Part[]` authored through " +
    "`defineModel`. Lean on spheres, icosahedra and cylinders for rounded, " +
    "friendly masses; save boxes for props and architecture.",
  "",
  "## Frame and scale",
  "- **Up:** `y` is up and `y=0` is the ground (the model sits on the floor).",
  "- **Forward:** `+Z` is the model's front (where the face looks).",
  "- **Scale:** 1 unit = 1 metre, but proportions are stylised, not " +
    "realistic — a creature is a toy-sized caricature (roughly 0.3-1.2 tall), " +
    "buildings are squat and chunky with oversized roofs and doors.",
  "",
  "## Appeal rules (the look)",
  "- **Silhouette first:** one big readable mass + a few bold secondary " +
    "shapes. If the outline does not say what it is, more detail will not.",
  "- **Proportions:** oversized heads (a character is 2-3 heads tall), " +
    "stubby limbs, big feet; buildings get fat walls, swollen roofs, tiny " +
    "windows and a too-big door or chimney.",
  "- **Eyes make it alive:** creatures and characters get the standard eye " +
    "recipe — a white sphere, a dark pupil sphere set slightly forward, " +
    "placed BIG and close together on a big head. Friendly, never scary.",
  "- **Colour:** warm, saturated, two or three main colours per model plus " +
    "a cream/off-white (`#f6efe3`) for bellies, trims and highlights. Use a " +
    "darker shade of the main colour for shading masses (ear insides, " +
    "underbellies, roof undersides). Avoid pure black and pure white.",
  "",
  "## Animation (mandatory)",
  "Every model ships at least TWO animations: a looping `idle` (a gentle " +
    "breathing bob, bounce, sway or hover — the model must never look dead) " +
    "and one looping signature move that sells its character (a tail wag, a " +
    "spin, a wave, a flame flicker, a carousel turn). One-shots (`loop: " +
    "false`) are welcome on top. Name every part a track drives; rotate and " +
    "scale about deliberate pivots (squash-and-stretch scales about the " +
    "ground contact, nods about the neck).",
  "",
  "## Part budget",
  "Creatures and characters <= 60 parts, props <= 40, vehicles <= 80, " +
    "buildings and rides <= 120. Prefer one big shape over many small bits.",
  "",
  "## Naming and tags",
  "Ids are kebab-case nouns (`baby-dragon`, `ice-cream-stand`). Tags are " +
    "lowercase and always include `mess`, `cute` and `animated`, plus " +
    "the category (`creature`, `building`, `vehicle`, `prop`, `character`, " +
    "`ride`) and subject tags.",
  "",
  "## Verify by looking",
  "You cannot judge a model from its code — render it headless " +
    "(`npm run preview-model -- <id>`) and look at the PNG before calling " +
    "it done.",
].join("\n");

/** Every batch, in production order (oldest first). */
export const BATCHES: Batch[] = [
  defineBatch({
    id: "standard",
    label: "Standard",
    for: "Age of MCP",
    generator: "unknown",
    style:
      "The original hand-built set in the game's standard low-poly look, from " +
      "before the library was regenerated. The least uniform baseline the later " +
      "batches fall back to.",
    description: withContract(),
  }),
  defineBatch({
    id: "fable",
    label: "Fable",
    for: "Age of MCP",
    generator: "fable",
    style:
      "A complete regeneration of the whole set, done kit by kit so each kit is " +
      "one homogeneous pass with a consistent palette and proportions.",
    description: withContract(
      "Generated per kit: tree, tropic, groundcover, mineral, critter (+safari, " +
      "+wildland), safari, wetland, wildland, village, industry, estate, roadwork " +
      "and building kits, plus a set of standalone objects without a kit.",
    ),
  }),
  defineBatch({
    id: "45-optimized",
    label: "45 Optimized",
    for: "Age of MCP",
    generator: "unknown",
    style:
      "The buildings re-cut and optimized for the game's 45-degree orthographic " +
      "camera, in the Fable redesign's architectural style: roof-led silhouettes " +
      "(the roof is the sign), open work yards, bold colour blocking and a tight " +
      "part budget. Staged buildings read foundation -> complete.",
    description: withContract(),
  }),
  defineBatch({
    id: "polished",
    label: "Polished",
    for: "Age of MCP",
    generator: "unknown",
    style:
      "A polish pass over a selection of objects — geometry and colour " +
      "corrections found in a gallery-wide review — today's in-game default for " +
      "the objects it covers.",
    description: withContract(),
  }),
  defineBatch({
    id: "future",
    label: "Future",
    for: "Age of MCP",
    generator: "unknown",
    style:
      "An art-only 'future' library generated in the Fable architectural style, " +
      "not wired into the game — purely models to draw from.",
    description: withContract(
      "Produced in three waves across categories A-U: resource deposits, crops " +
      "and flora, industry and extraction buildings, civic/prestige buildings, " +
      "biome scenery and animals (wave 1); raw materials, goods stockpiles, " +
      "shared processing, logistics, yard props, civic amenities and " +
      "multi-purpose flora/animals (wave 2); vehicles, crossings, forestry, " +
      "parked war and defense, city decoration and lighting (wave 3).",
    ),
  }),
  defineBatch({
    id: "electric",
    label: "Electric",
    for: "Age of MCP",
    generator: "unknown",
    style:
      "An electricity / power-grid series in the standard low-poly look: the " +
      "same flat-coloured, few-primitive language, applied to the chain that " +
      "makes and moves electric power. Staged buildings read foundation -> " +
      "complete like the rest of the set; props are single-variant.",
    description: withContract(
      "Generation (coal/gas/nuclear plants, cooling tower, hydro dam, wind " +
      "turbine, solar, geothermal, biomass, tidal, generators), transmission and " +
      "distribution (pylons, poles, line spans, substation, transformers, " +
      "switchgear, breakers, insulators), storage (battery banks, grid battery, " +
      "capacitors, flywheel), control and metering, lighting and consumer " +
      "fixtures (street lamps, floodlights, EV charging, meters), cabling and " +
      "telecom/compute (cable reels, conduit, radio tower, dish, server rack, " +
      "data center).",
    ),
    date: "2026-06",
  }),
  defineBatch({
    id: "justtest",
    label: "Just Test",
    for: "Model-authoring experiments",
    generator: "claude",
    style:
      "An experimental sandbox series, not tied to any game: each model is a " +
      "probe of how well an agent can design and animate a low-poly model from a " +
      "single prompt. The brief is open — aim for an appealing, characterful " +
      "design with a rich, expressive set of animations — and each model is " +
      "reviewed on its own merits rather than against a game contract.",
    description:
      "A test bench for model generation. Unlike the Age of MCP batches, these " +
      "models carry no game contract: they are free-standing low-poly models made " +
      "to gauge design and animation quality. The same library rules still hold " +
      "(flat-coloured primitives, y=0 ground, named parts for animation), but the " +
      "footprint, palette and motion are chosen per model to look its best.",
    date: "2026-06",
  }),
  defineBatch({
    id: "wwi",
    label: "Great War",
    for: "Age of MCP",
    generator: "fable-5",
    style:
      "A World-War-One themed content series in the Fable architectural " +
      "style: trench warfare, early armour and aviation, field logistics and " +
      "the home-front chain behind them. Muted khaki/field-grey materials with " +
      "the shared functional roof palette on buildings; staged buildings read " +
      "foundation -> complete; vehicles, props, scenery, characters, creatures " +
      "and goods are single-variant (plus visual variants for diversity).",
    description: withContract(
      "The Great War series: staged military buildings (command bunker, " +
      "barracks, field hospital, munitions factory, depots, hangar, airship " +
      "mast, pillbox, workshops), early war machines (rhomboid and light " +
      "tanks, armoured car, biplanes, observation balloon, trucks and staff " +
      "car), front-line props (howitzer, machine-gun nest, mortar, barbed " +
      "wire, sandbags, tank traps, searchlight), trench scenery (trench " +
      "segments, dugout, craters, ruins, shattered trees), rig-compatible " +
      "figures (infantryman, officer, medic), animals of the front (messenger " +
      "pigeon, war horse, trench rat) and carriable supplies (ration crate, " +
      "field medkit).",
    ),
    date: "2026-07",
  }),
  defineBatch({
    id: "modern",
    label: "Modern Living",
    for: "Modular modern city & interior construction",
    generator: "fable-5",
    style:
      "A present-day construction kit: contemporary structure pieces (walls, " +
      "floors, doors, windows, stairs, roofs, columns, domes), furniture, " +
      "appliances, bathroom/kitchen fixtures, decor and outdoor/city pieces " +
      "(gardens, paths, roads, street furniture), all authored on a shared " +
      "1-metre tile grid so whole real buildings and streets assemble from " +
      "snapping tiles. Muted contemporary palette; single-variant by default.",
    description: MODERN_CONTRACT,
    date: "2026-07",
  }),
  defineBatch({
    id: "cars",
    label: "Cars",
    for: "Real-world car showcase",
    generator: "fable-5",
    style:
      "Real production cars at true 1 unit = 1 metre scale, pushed to the " +
      "most detailed, realistic read the low-poly language allows: spec-sheet " +
      "proportions, full fascia, glazing and trim detail, wheels with rims, " +
      "spokes and brake discs, a 'paint' recolour slot and a spinning-wheel " +
      "drive animation.",
    description: CARS_CONTRACT,
    date: "2026-07",
  }),
  defineBatch({
    id: "mess",
    label: "Mess",
    for: "General stylised game art",
    generator: "fable-5",
    style:
      "A Pixar-like 3D-animation-film feel: chunky rounded silhouettes, " +
      "exaggerated toy proportions (big heads, big eyes, stubby limbs, " +
      "swollen roofs), warm saturated two-tone colours with cream " +
      "highlights, and life through motion — every model carries an idle " +
      "plus a signature animation. Fifty new subjects across cute " +
      "creatures, whimsical buildings, fairground rides, adventure " +
      "vehicles and magic props.",
    description: STORYBOOK_CONTRACT,
    date: "2026-07",
  }),
  defineBatch({
    id: "characters",
    label: "Characters",
    for: "Age of MCP",
    generator: "unknown",
    style:
      "The character / actor batch: a reusable actor (the worker rig) plus the " +
      "first models authored for the interaction-animation format — a model " +
      "publishes stations and an interaction couples the actor's motion to the " +
      "model (a worker drawing from a well).",
    description: withContract(
      "Introduces Station + Interaction + the actor rig: an interaction is a " +
      "two-body animation moving an engaged actor at a station, optionally coupled " +
      "to the model's own parts. Ported from age-of-mcp's station/program system as " +
      "declarative keyframes.",
    ),
  }),
];

/** Batch lookup by id — the index a model's `batchId` resolves through. */
export const batchById: ReadonlyMap<string, Batch> = new Map(
  BATCHES.map((b) => [b.id, b]),
);

/** The batch a model belongs to, or `undefined` if it has none / an unknown id. */
export function batchOf(model: { batchId?: string }): Batch | undefined {
  return model.batchId ? batchById.get(model.batchId) : undefined;
}
